import { SettleResult, SubmitFn, isNavigatingResult, settle } from './settle'
import React, { useEffect, useRef, useState } from 'react'
import { CardFormInstance } from './adapters/types'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { getCardAdapter } from './adapters/card'
import { useHistory } from 'react-router-dom'
import Popup from 'components/popups/Popup'
import { STATUS } from 'utils/constants'
import { useScript } from './loadScript'

/** najdluzej tyle trzymamy loader, zanim odslonimy formularz mimo braku iframe'a */
const FORM_VISIBLE_TIMEOUT = 8_000
const FORM_VISIBLE_INTERVAL = 100

/**
 * `render()` operatora wraca natychmiast, a iframe pojawia sie dopiero chwile pozniej -
 * dlatego sam koniec `mount()` to za slaby sygnal i loader gasl nad pusta ramka.
 * Pytamy wiec DOM, bo to jedyna oznaka niezalezna od SDK: iframe istnieje i ma wysokosc.
 * Po limicie odslaniamy formularz mimo wszystko - lepiej pokazac za wczesnie niz wcale.
 */
const waitForVisibleForm = (host: HTMLElement, isAlive: () => boolean) => {
  return new Promise<void>(resolve => {
    const started = Date.now()

    const check = () => {
      const iframe = host.querySelector('iframe')
      const isVisible = !!iframe && iframe.offsetHeight > 0
      if (isVisible || !isAlive() || Date.now() - started > FORM_VISIBLE_TIMEOUT) {
        clearInterval(interval)
        resolve()
      }
    }

    const interval = setInterval(check, FORM_VISIBLE_INTERVAL)
    check()
  })
}

interface CardPopupProps {
  cardForm: payments.CardForm
  /** zgoda na zapis metody -> tokenizujemy typem `multi` */
  saveForRecurring?: boolean
  /** buduje body rozliczenia; `extra` to pole z tokenem karty */
  getBody: (extra: Record<string, any>) => any
  /** wywołane raz: z wynikiem rozliczenia albo bez niczego, gdy klient zamknął popup */
  onResult: (result?: SettleResult) => void
  /** inny endpoint niż rozliczenie (np. dodanie metody w Koncie) - domyślnie `settle` */
  submit?: SubmitFn
  /** etykieta przycisku zamiast „Zapłać” */
  submitText?: string
  /** dodatkowa informacja pod opisem, np. o zwracanej kwocie weryfikacyjnej */
  note?: React.ReactNode
  /** zamknięcie popupu, gdy nie siedzi we własnym wpisie historii - domyślnie goBack() */
  close?: () => void
}

const CardPopup: React.FC<CardPopupProps> = ({ cardForm, saveForRecurring, getBody, onResult, submit = settle, submitText, note, close }) => {
  const [status, setStatus] = useState<string>(STATUS.idle)
  const [isLoading, setLoading] = useState(false)
  // `mounting` -> montujemy, `ready` -> można płacić, `blocked` -> adapter JAWNIE mówi, że nie.
  // Brak sygnału `onReady` NIE jest awarią: nie każde SDK go wysyła, a formularz działa.
  const [formState, setFormState] = useState<'mounting' | 'ready' | 'blocked'>('mounting')
  const [warning, setWarning] = useState('')

  const instance = useRef<CardFormInstance | null>(null)
  const host = useRef<HTMLDivElement>(null)
  const isActive = useRef(true)

  const isFilling = status !== STATUS.pending

  const sdk = useScript(cardForm.sdkUrl)
  const { goBack } = useHistory()
  const closePopup = close || goBack
  const { t, lang } = useTranslation('payment')

  useEffect(() => {
    return () => {
      isActive.current = false
    }
  }, [])

  useEffect(() => {
    if (sdk !== 'ready' || !host.current) return

    // kopia referencji na czas zycia efektu - w sprzataniu host.current moze byc juz czym innym
    const container = host.current
    const adapter = getCardAdapter(cardForm)
    if (!adapter) {
      setWarning(t('cardFormFailed'))
      return
    }

    let alive = true
    let created: CardFormInstance | null = null
    setFormState('mounting')

    // mount bywa asynchroniczny - Przelewy24 pobiera najpierw sesję z naszego backendu
    const mount = async () => {
      try {
        created = adapter.create(cardForm, {
          lang,
          placeholders: {
            number: t('cardNumber'),
            date: t('cardExpiry'),
            cvv: t('cardCvv')
          },
          onReady: ready => {
            if (!alive) return
            setFormState(ready ? 'ready' : 'blocked')
          }
        })
        instance.current = created
        await created.mount(container)

        // Popup zamkniety w trakcie montazu: sprzatanie efektu poszlo, zanim `created`
        // istnialo, wiec destroy() nigdy by sie nie wykonal - adapter zostalby z zywym
        // nasluchem i iframe'em w oderwanym kontenerze. Sprzatamy sami.
        if (!alive) {
          created.destroy()
          return
        }

        await waitForVisibleForm(container, () => alive)

        // Widoczny formularz wystarczy, żeby pozwolić płacić. Adapter może to potem cofnąć
        // przez onReady(false) - i tylko takie jawne "jeszcze nie" blokuje przycisk.
        if (alive) setFormState(prev => (prev === 'blocked' ? prev : 'ready'))

      } catch (error) {
        console.error(error)
        if (alive) setWarning(t('cardFormFailed'))
      }
    }
    mount()

    return () => {
      alive = false
      created?.destroy()
      // pas i szelki: adapter dostaje szanse posprzatac po swojemu, ale kontener
      // ma zostac pusty niezaleznie od tego, jak grzeczne jest SDK operatora
      container.innerHTML = ''
      if (instance.current === created) instance.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk, cardForm, lang])

  /**
   * PayU oddaje gotowe zdanie po polsku, Przelewy24 kod błędu (`regulationsIsRequired`,
   * `invalidCardHolder`, ...). Kod próbujemy przetłumaczyć, a gdy nie znamy - pokazujemy
   * komunikat ogólny zamiast surowego identyfikatora.
   */
  const getErrorMessage = (error: any) => {
    const reason = error?.reason
    if (reason) {
      const translated = t(`cardError.${reason}`)
      if (!translated.startsWith('{')) return translated
      console.warn('[payments] nieznany kod błędu formularza karty:', reason)
      return t('cardTokenizeFailed')
    }
    // gotowy komunikat operatora nadaje się do pokazania wprost
    if (error?.message && error.message !== error?.code) return error.message
    return t('cardTokenizeFailed')
  }

  const hidePopup = () => {
    isActive.current = false
    onResult()
    closePopup()
  }

  const send = async () => {
    if (isLoading || status === STATUS.pending) return
    if (!instance.current) {
      setWarning(t('cardFormFailed'))
      return
    }

    setWarning('')
    setLoading(true)

    try {
      const token = await instance.current.tokenize(saveForRecurring ? 'multi' : 'single')
      const body = await getBody({ [cardForm.tokenField]: token })
      if (!body) {
        setLoading(false)
        return
      }

      const result = await submit(body, {
        isActive: () => isActive.current,
        onPending: () => setStatus(STATUS.pending)
      })
      if (!isActive.current) return

      setLoading(false)
      if (result.type === 'error' || result.type === 'failure') {
        setStatus(STATUS.failed)
        setWarning(result.handled ? '' : (result.message || t('paymentFailed')))
        return
      }

      isActive.current = false
      onResult(result)
      // przy redirect/pending kontener sam nawiguje - goBack() w tym samym ticku
      // anulowałby tę nawigację i klient zostałby na ekranie płatności
      if (!isNavigatingResult(result)) closePopup()

    } catch (error: any) {
      if (!isActive.current) return
      setLoading(false)
      setStatus(STATUS.failed)
      setWarning(getErrorMessage(error))
    }
  }

  return (
    <Popup
      centered
      close={hidePopup}>
      <div className='popup__card'>
        <div className='card__title'>
          <i className='icon icon--payments s64' />
          <div>{t(status === STATUS.pending ? 'processingPayment' : 'cardData')}</div>
        </div>

        <p>
          <span>{t(status === STATUS.pending ? 'waitingForPaymentStatus' : 'enterCardData')}</span>
          {warning ? <span className='--warning'> - {warning}</span> : null}
        </p>
        {note && status !== STATUS.pending && <p className='popup__note'>{note}</p>}

        <div
          hidden={status === STATUS.pending}
          className='card__form'
          data-provider={cardForm.provider}>
          {/* loader przez CAŁY montaż: pobranie SDK, sesja z backendu i render iframe'a -
              wcześniej gasł po samym skrypcie i klient patrzył na zwiniętą ramkę */}
          {(sdk === 'loading' || formState === 'mounting') && <div className='card__form__skeleton' />}
          <div ref={host} />
        </div>

        {isFilling &&
          <p className='card__secure-note'>
            <i className='icon icon--lock s14 cSecText' />
            <span>{t('cardDataNotStored')}</span>
          </p>}

        {status !== STATUS.pending
          ? <div className='popup__buttons'>
            <ArrowBtn
              enlarged
              text={submitText || t('pay')}
              isLoading={isLoading}
              disabled={sdk !== 'ready' || formState !== 'ready'}
              action={send} />
            <BackBtn
              text={t('cancel')}
              action={hidePopup} />
          </div>
          : <ArrowBtn
            enlarged
            text={t('cancel')}
            className='--black'
            action={hidePopup} />}
      </div>
    </Popup>
  )
}

export default CardPopup
